import { TFunction } from 'i18next';
import moment, { Moment } from 'moment';
import { IconStatusType } from 'src/js/model/icon-status-type';
import { ipOrHostname } from 'src/js/validator/validator';
import * as yup from 'yup';

import { timezoneNames } from './timezone-helper';
import { GenericResponse } from 'src/js/model/api/response/generic-response';

export enum PTP_ANNOUNCEMENT_INTERVAL {
  ANNOUNCE_INTERVAL_125_MSEC = -3,
  ANNOUNCE_INTERVAL_250_MSEC = -2,
  ANNOUNCE_INTERVAL_500_MSEC = -1,
  ANNOUNCE_INTERVAL_1_SEC = 0,
  ANNOUNCE_INTERVAL_2_SEC = 1,
}

export enum PTP_SYNC_INTERVAL {
  SYNC_INTERVAL_7812500_NSEC = -7,
  SYNC_INTERVAL_15625_USEC = -6,
  SYNC_INTERVAL_31250_USEC = -5,
  SYNC_INTERVAL_62500_USEC = -4,
  SYNC_INTERVAL_125_MSEC = -3,
  SYNC_INTERVAL_250_MSEC = -2,
  SYNC_INTERVAL_500_MSEC = -1,
  SYNC_INTERVAL_1_SEC = 0,
  SYNC_INTERVAL_2_SEC = 1,
}

export enum PTP_DELAY_MECHANISM {
  AUTO = 0,
  E2E,
  P2P,
}

export interface DateTimeResponse extends GenericResponse {
  timezone: string;
  ntp: boolean;
  server: string;
  offset: number;
  dst?: number;
  chronydRunning: boolean;
  time: number; // unix timestamp (in seconds)
  clockId?: string;
  clockLocked?: boolean;
  clockMode?: 'TS_CONV' | 'RESTAMP';
}

//TODO rename to DateTimeViewModel
export interface DateTime {
  timezone: string;
  ntp: boolean;
  server: string;
  chronydRunning: boolean;
  time: Moment;
  clockId?: string;
  clockLocked?: boolean;
  clockMode?: 'TS_CONV' | 'RESTAMP';
  clockLedIcon?: IconStatusType;
  clockLockState?: string;
  // ptp
  domainNumberNmos?: number | string;
  domainNumber?: number | string;
  announcementTimeout?: number | string;
  announcementTimeoutNmos?: number | string;
  announcementInterval?: number | string;
  announcementIntervalNmos?: number | string;
  delayMechanism?: PTP_DELAY_MECHANISM;
  syncInterval?: PTP_SYNC_INTERVAL;
}

// UNUSED
export const createDefaultDateTime = (): DateTime => {
  return {
    timezone: '',
    ntp: false,
    server: '',
    chronydRunning: false,
    time: undefined,
    clockLedIcon: IconStatusType.INACTIVE,
    clockLockState: 'UNLOCKED',
    clockId: '00:00:00:00:00:00:00:00',
    domainNumber: '',
    domainNumberNmos: '',
    announcementInterval: '',
    announcementIntervalNmos: '',
    announcementTimeout: '',
    announcementTimeoutNmos: '',
    delayMechanism: PTP_DELAY_MECHANISM.AUTO,
    syncInterval: PTP_SYNC_INTERVAL.SYNC_INTERVAL_1_SEC,
  };
};

export const mapDateTime = (response: DateTimeResponse): DateTime => ({
  ...response,
  time: moment.tz(moment.unix(response.time), response.timezone),
  clockLedIcon: response.clockLocked ? IconStatusType.OK : IconStatusType.INACTIVE,
  clockLockState: response.clockLocked ? 'LOCKED' : 'UNLOCKED',
});

/*
 * Schema for the form
 */
export const dateAndTimeSchema = (t: TFunction) =>
  yup.object().shape({
    timezone: yup.mixed().oneOf(
      Array.from(timezoneNames).map((entry) => entry[0]),
      t('validation.timezone'),
    ),
    server: yup.string().when('ntp', {
      is: true,
      then: ipOrHostname(t).required(t('validation.required')),
    }),
    time: yup.date().when('ntp', {
      is: false,
      then: yup.date().required(t('validation.required')),
    }),
  });

/*
 * Schema for the form
 */
export const dateAndTimeWithPtpSchema = (t: TFunction) =>
  dateAndTimeSchema(t).shape({
    domainNumber: yup
      .number()
      .typeError(t('validation.dateTime.ptp.domainNumber'))
      .required(t('validation.dateTime.ptp.domainNumber'))
      .min(0, t('validation.dateTime.ptp.domainNumber'))
      .max(127, t('validation.dateTime.ptp.domainNumber')),
    announcementTimeout: yup
      .number()
      .typeError(t('validation.dateTime.ptp.announcementTimeout'))
      .required(t('validation.dateTime.ptp.announcementTimeout'))
      .min(2, t('validation.dateTime.ptp.announcementTimeout'))
      .max(10, t('validation.dateTime.ptp.announcementTimeout')),
  });
